//  Angular imports
import { AfterViewInit, Component, ElementRef, OnDestroy, TemplateRef, contentChild, effect, inject, input, output, signal, untracked, viewChild, viewChildren } from '@angular/core';
//  Application imports
import { DOMEvent } from '../models/dom-event';
import { DOMEventType } from '../models/dom-event-type';
import { DOMUtilsService } from '../services/dom-utils.service';
import { Selectable } from '../models/selectable';
//  Third party imports
import { debounceTime, distinctUntilChanged, filter, map, takeWhile, tap } from 'rxjs/operators';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { Key } from 'ts-key-enum';
import { FormsModule } from '@angular/forms';
import { NgClass, NgStyle, NgFor, NgTemplateOutlet } from '@angular/common';

/* eslint-disable  @typescript-eslint/no-explicit-any */
const sectionGap: number = 16;

(HTMLElement.prototype as any).clickOutside =
    function (whileCondition: () => boolean, filters: Array<() => boolean> = []): Observable<boolean> {
        let clickOutside$: Observable<Event> = fromEvent(document, 'click').pipe(
            takeWhile(whileCondition)
        );

        filters.forEach(
            (filterFunction: () => boolean): Observable<Event> => clickOutside$ = clickOutside$.pipe(
                filter(filterFunction)
            )
        );

        return clickOutside$
            .pipe(
                map(
                    (event: Event): boolean => !this.contains(event.target)
                )
            );
    };

@Component({
    selector: 'zf-dropdown',
    templateUrl: './zf-dropdown.component.html',
    styleUrls: ['./zf-dropdown.component.scss'],
    imports: [FormsModule, NgClass, NgStyle, NgFor, NgTemplateOutlet]
})
export class ZFDropdownComponent implements AfterViewInit, OnDestroy {
    private dropDownContainer = inject(ElementRef);
    private domUtilsService = inject(DOMUtilsService);

    // Bind droped down HTML component with following properties
    public readonly dropDownWidth = signal<number>(undefined);
    public readonly dropDownMaxHeight = signal<number>(undefined);
    public readonly visibility = signal<'hidden' | 'visible'>('hidden');

    // dropdown elements after filtering
    public readonly filteredElements = signal<Array<Selectable>>([]);
    // search expression to match with elements
    public readonly filterExpression = signal<string>(undefined);
    public readonly open = signal<boolean>(false);

    public readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');
    // id for the input
    public readonly inputId = input<string>('');
    // placeholder text for the input
    public readonly placeholder = input<string>();
    // whether the input is required
    public readonly required = input<boolean>(false);
    // whether the input is disabled
    public readonly disabled = input<boolean>(false);
    // whether contents should be hidden
    public readonly hidden = input<boolean>(false);
    // initial value
    public readonly value = input<any>();

    public readonly maxHeight = input<number>(null);
    // transcluded template to be rendered
    public readonly dropdownElementTemplate = contentChild<TemplateRef<any>>('zfDropdownElementTemplate');

    // dropdown elements before filtering
    public readonly elements = input<Array<Selectable>>([]);

    public readonly caseSensitive = input<boolean>(false);
    // an array of strings that, should the input value match any of them, take precedence over list values
    public readonly priorityStrings = input<Array<string>>([]);
    public readonly selectElement = output<any>();
    public readonly freeHand = output<string>();

    private readonly delay: number = 100;

    // outermost div
    private readonly host = viewChild<ElementRef<HTMLFormElement>>('host');
    // DOM representation of elements
    private readonly filteredDOMElements = viewChildren<ElementRef<HTMLLIElement>>('filteredDOMElement');

    private keyDown$: Observable<KeyboardEvent>;
    private keyUp$: Observable<KeyboardEvent>;
    private inputClick$: Observable<MouseEvent>;
    private subscriptions: Array<Subscription> = [];
    // helper map for efficient lookup
    private lookupMap: { [key: string]: Selectable[] } = {};
    // delay before rerendering dropdown
    private readonly debounce: number = 200;
    private domChangeSubscription: Subscription;

    // Re-derive the list and the displayed value whenever either input changes. The
    // value can be bound before the element list arrives, in which case there is
    // nothing to match it against yet, so the list turning up has to re-run the
    // lookup. This runs before the view is checked, so the [(ngModel)] on the input
    // sees a settled filterExpression.
    private readonly syncInputs = effect((): void => {
        this.elements();
        this.value();
        untracked((): void => {
            this.initLookupMap();
            this.filterList(null);
            this.setValue();
        });
    });

    public ngAfterViewInit(): void {
        const input: HTMLInputElement = this.inputElement().nativeElement;
        this.keyDown$ = fromEvent<KeyboardEvent>(input, 'keydown');
        this.keyUp$ = fromEvent<KeyboardEvent>(input, 'keyup');
        this.inputClick$ = fromEvent<MouseEvent>(input, 'click');
        this.subscriptions.push(this.subscribeToInputKeyup());
        this.subscriptions.push(this.subscribeToInputClick());
    }

    public isOpen(): boolean {
        return this.open();
    }

    public enter(): void {
        // check for priority string
        const rawValue: string = this.inputElement().nativeElement.value;
        const inputValue: string = this.caseSensitive() ? rawValue : rawValue.toLowerCase();
        const priorityString: string = this.priorityStrings().map(
            (priority: string): string => this.caseSensitive() ? priority : priority.toLowerCase()
        ).find(
            (priority: string): boolean => priority === inputValue
        );

        if (priorityString) {
            this.freeHand.emit(priorityString);

            // check if elements are filtered to a single one
        } else if (this.filteredElements().length === 1) {
            this.select(this.filteredElements()[0]);
        }
    }

    public select(selectedElement: Selectable, event: MouseEvent = null): void {
        if (event) { event.stopPropagation(); }
        this.closeDropdown();
        this.inputElement().nativeElement.value = selectedElement.display;
        this.filterList(null);
        this.selectElement.emit(selectedElement.value);
    }

    public onKeydown(event: KeyboardEvent, item: Selectable): void {
        if (!event.key || ![Key.Enter, Key.Escape, Key.ArrowUp, Key.ArrowDown].includes(event.key as Key)) {
            return;
        }
        event.preventDefault();

        if (event.key === Key.Enter) {
            this.select(item);
        } else if (event.key === Key.Escape) {
            this.closeDropdown();
            this.inputElement().nativeElement.focus();
        } else {
            this.proceedInList(event);
        }
    }

    public resetDropDownElement(): void {
        const container: HTMLElement = this.dropDownContainer.nativeElement;

        this.setDimensionsAndPosition(container);

        if (this.open()) {
            this.closeDropdown();
            setTimeout((): void =>
                this.openDropdown()
            );
        }
    }

    public ngOnDestroy(): void {
        this.destroyDropDownListener();
        if (!!this.subscriptions && !!this.subscriptions.length) {
            this.subscriptions.forEach(
                (subscription: Subscription): void => subscription.unsubscribe()
            );
        }
    }

    // filtering

    private setValue(): void {
        const value: any = this.value();
        if (!!value || value === false || value === 0) {
            const foundElement: Selectable = this.elements().find(
                (element: Selectable): boolean => {
                    if (!this.caseSensitive() && typeof value === 'string' && typeof element.value === 'string') {
                        return value.toLowerCase() === element.value.toLowerCase();
                    } else {
                        return value.toString() === element.value.toString();
                    }
                }
            );
            if (foundElement) {
                this.filterExpression.set(foundElement.display);
            }
        }
    }

    private initLookupMap(): void {
        this.lookupMap = {};
        this.elements().forEach(
            (element: Selectable): void => element.searchables
                .map((searchable: string): string => searchable.toLowerCase())
                .forEach((searchable: string): any =>
                    searchable in this.lookupMap ?
                        this.lookupMap[searchable].push(element) :
                        this.lookupMap[searchable] = [element]));
    }

    private filterList(filterExpression: string): void {
        if (!filterExpression) {
            this.filteredElements.set(this.elements());
            return;
        }
        // convert search expression to lowercase
        const filterExpressionLower: string = filterExpression.toLowerCase();
        // look up elements by their display value
        this.filteredElements.set(this.filterByDisplay(filterExpressionLower));
        // create set to store used display values
        const filteredSet: Set<string> = this.initFilteredSet();
        // look up elements by their searchables
        this.filterBySearchables(filterExpressionLower, filteredSet);

        if (!this.filteredElements().length) {
            this.closeDropdown();
        }
    }

    private filterByDisplay(filterExpressionLower: string): Array<Selectable> {
        return this.elements()
            .filter(
                (dropdownElement: Selectable): boolean => dropdownElement.display.toLowerCase().indexOf(filterExpressionLower) > -1
            )
            .map(
                (dropdownElement: Selectable): Selectable => new Selectable(
                    dropdownElement.display,
                    dropdownElement.value,
                    ...[]
                )
            );
    }

    private initFilteredSet(): Set<string> {
        const set: Set<string> = new Set();
        this.filteredElements().map(
            (filteredElement: Selectable): any => filteredElement.display
        ).forEach(
            (display: string): Set<string> => set.add(display)
        );
        return set;
    }

    private filterBySearchables(filterExpressionLower: string, filteredSet: Set<string>): void {
        Object.keys(this.lookupMap)
            .filter((key: string): boolean => key.indexOf(filterExpressionLower) > -1)
            .forEach((key: string): void => {
                const filteredElements: Array<Selectable> = this.lookupMap[key].map(
                    (filteredElement: Selectable): Selectable => new Selectable(
                        filteredElement.display,
                        filteredElement.value,
                        key
                    )
                ).filter((el: Selectable): boolean => !filteredSet.has(el.display));
                this.filteredElements.update((current: Array<Selectable>): Array<Selectable> => [...current, ...filteredElements]);
            });
    }

    // open, close, orientation and size

    private openDropdown(): void {
        if (!!this.filteredElements().length && !this.open()) {
            this.open.set(true);
            this.visibility.set('visible');
            this.initDropDownListener();
            this.setDimensionsAndPosition(this.dropDownContainer.nativeElement);
            this.subscriptions.push(this.subscribeToClickOutside());
            this.subscriptions.push(this.subscribeToEdit());
            this.subscriptions.push(this.subscribeToListNavigation());
        }
    }

    private closeDropdown(): void {
        if (this.open()) {
            this.open.set(false);
            this.visibility.set('hidden');
            this.destroyDropDownListener();
        }
    }

    // listen to DOM changes and change status accordingly
    private initDropDownListener(): void {
        this.destroyDropDownListener(); // make sure it is not re-hooked
        this.domChangeSubscription = this.domUtilsService.domChanged$
            .pipe(
                filter((domEvent: DOMEvent): boolean => // ignore irrelevant events
                    domEvent.type === DOMEventType.RESIZE ||
                    domEvent.type === DOMEventType.SCROLL
                ),
                debounceTime(this.debounce)
            )
            .subscribe((): void => this.resetDropDownElement());
    }

    private destroyDropDownListener(): void {
        if (this.domChangeSubscription) {
            this.domChangeSubscription.unsubscribe();
        }
    }

    private setDimensionsAndPosition(container: HTMLElement): void {
        // calculate dimensions
        const before: number = container.getBoundingClientRect().top;
        const after: number = window.innerHeight - container.getBoundingClientRect().bottom;

        if (before < 0) {
            // close menu if container is under menu container
            this.closeDropdown();
        }

        // set calculated values
        this.dropDownMaxHeight.set(this.maxHeight()
            ? this.maxHeight()
            : Math.max(before, after) - sectionGap); // minus some breathing space (more than horizontal scrollbar 15 / 17px)
        this.dropDownWidth.set(container.getBoundingClientRect().width);
    }

    private subscribeToEdit(): Subscription {
        return this.keyDown$.
            subscribe(
                (event: KeyboardEvent): void => {
                    // allow normal editing when there is content
                    // otherwise let the event propagate
                    // and parent components deal with it
                    if ((event.key === Key.Delete || event.key === Key.Backspace) && !!this.inputElement().nativeElement.value) {
                        event.stopPropagation();
                    }
                }
            );
    }

    private subscribeToListNavigation(): Subscription {
        return this.keyDown$
            .pipe(
                // navigate only as long as the dropdown is open
                takeWhile((): boolean => this.open()),
                // navigate only with the open and down arrows and only if there is something in the list
                filter((event: KeyboardEvent): boolean =>
                    (
                        event.key === Key.ArrowUp ||
                        event.key === Key.ArrowDown
                    ) &&
                    !!this.filteredElements().length
                )
            ).subscribe((event: KeyboardEvent): void => {
                event.preventDefault();

                const focusIndex: number = event.key === Key.ArrowDown ? 0 : this.filteredElements().length - 1;
                const itemsArr: ReadonlyArray<ElementRef<HTMLLIElement>> = this.filteredDOMElements();
                const focusItem: HTMLLIElement = itemsArr[focusIndex].nativeElement;
                focusItem.focus();
            });
    }

    private subscribeToInputKeyup(): Subscription {
        return this.keyUp$.pipe(
            tap((event: KeyboardEvent): void => {
                if (event.key === Key.Escape) {
                    this.closeDropdown();
                } else if (event.key === Key.Enter) {
                    this.freeHand.emit(this.inputElement().nativeElement.value);
                    this.filterList(null);
                    this.closeDropdown();
                } else if (event.key !== Key.ArrowLeft && event.key !== Key.ArrowRight) {
                    this.openDropdown();
                }
            }),
            debounceTime(this.delay),
            map((): string => this.filterExpression()),
            distinctUntilChanged(
                (previous: string, current: string): boolean => previous === current
            )
        ).subscribe(
            (filterExpression: string): void => this.filterList(filterExpression)
        );
    }

    private subscribeToInputClick(): Subscription {
        return this.inputClick$
            .subscribe(
                (): void => {
                    if (!this.open()) {
                        this.openDropdown();
                        this.inputElement().nativeElement.select();
                    }
                }
            );
    }

    private subscribeToClickOutside(): Subscription {
        return (this.host().nativeElement as any).clickOutside(this.isOpen.bind(this))
            .subscribe(
                (clickedOutside: boolean): void => {
                    if (clickedOutside) { this.closeDropdown(); }
                }
            );
    }

    private proceedInList(event: KeyboardEvent): void {
        event.preventDefault();

        const currentFocused: HTMLElement = event.target as HTMLElement;
        if (event.key === Key.ArrowDown) {
            this.focusNext(currentFocused);
        } else {
            this.focusPrevious(currentFocused);
        }
    }

    private focusNext(currentFocused: HTMLElement): void {
        if (!currentFocused || !currentFocused.parentElement) {
            return;
        }

        if (currentFocused.nextElementSibling) {
            (currentFocused.nextElementSibling as HTMLElement).focus();
        } else {
            (currentFocused.parentElement.children[0] as HTMLElement).focus();
        }
    }

    private focusPrevious(currentFocused: HTMLElement): void {
        if (currentFocused.previousElementSibling) {
            (currentFocused.previousElementSibling as HTMLElement).focus();
        } else {
            const lastIndex: number = currentFocused.parentElement.children.length - 1;
            (currentFocused.parentElement.children[lastIndex] as HTMLElement).focus();
        }
    }
}
